import { useLocation, useSearchParams } from 'react-router';
import { CreditAnalysisStatus } from '@/api/commercial/credit-analysis';
import type { LocalProposal } from '@/context/create-proposal-context';
import { useCreditAnalysisExpiration } from '@/hooks/commercial/use-credit-analysis-expiration';
import { useProposalCreditAnalysisResolution } from '@/hooks/commercial/use-proposal-credit-analysis-resolution';

interface Step {
  id: string;
  title: string;
  href: string;
  completed?: boolean;
  disabled?: boolean;
  isActive?: boolean;
}

const stepIds = ['client', 'credit-analysis', 'cart', 'config', 'delivery', 'resume'] as const;
const stepTitles: Record<string, string> = {
  client: 'CLIENTE',
  'credit-analysis': 'ANÁLISE DE CRÉDITO',
  cart: 'CARRINHO',
  config: 'CONFIGURAÇÕES',
  delivery: 'ENTREGA',
  resume: 'RESUMO',
};

export function useProposalStepProgress({
  proposal,
  isDeliveryComplete,
  isCartStepValid,
  isConfigurationStepValid,
}: {
  proposal: LocalProposal | null;
  isDeliveryComplete?: boolean;
  isCartStepValid?: boolean;
  isConfigurationStepValid?: boolean;
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || 'client';
  const { data: creditResolution, isSuccess: isCreditResolutionSuccess } =
    useProposalCreditAnalysisResolution(proposal?.id);
  const { isExpired } = useCreditAnalysisExpiration(creditResolution?.analysis, proposal?.status);
  const creditStatus = creditResolution?.analysis?.status;
  const isCreditAnalysisCompleted =
    isCreditResolutionSuccess &&
    creditResolution?.isValid === true &&
    !isExpired &&
    (creditStatus === CreditAnalysisStatus.APPROVED ||
      creditStatus === CreditAnalysisStatus.PARTIALLY_APPROVED);

  const getBasePath = () => {
    const currentPath = location.pathname;
    if (currentPath.includes('/update/')) {
      return currentPath.split('?')[0];
    }
    return '/commercial/proposal/form/create';
  };

  const basePath = getBasePath();

  const steps: Step[] = stepIds.map((id) => ({
    id,
    title: stepTitles[id],
    href: `${basePath}?step=${id}`,
  }));

  const getCurrentStepIndex = () => {
    const stepIndex = steps.findIndex((s) => s.id === currentStep);
    return stepIndex >= 0 ? stepIndex : 0;
  };

  const currentIndex = getCurrentStepIndex();

  // Pre-compute completion status for each step (used for ordering gates)
  const isStepCompleted = (stepId: string): boolean => {
    switch (stepId) {
      case 'client':
        return !!proposal?.id;
      case 'credit-analysis':
        return !!proposal?.creditAnalysisId;
      case 'cart':
        return !!isCartStepValid;
      case 'config':
        return !!isConfigurationStepValid;
      case 'delivery':
        return !!isDeliveryComplete;
      default:
        return false;
    }
  };

  return steps.map((step, index) => {
    const isActive = index === currentIndex;
    const isCompleted = isStepCompleted(step.id);
    const isFutureStep = !isActive && !isCompleted;

    // Content-based gate
    let contentDisabled = false;
    if (step.id === 'credit-analysis') contentDisabled = !proposal?.id;
    if (step.id === 'config') contentDisabled = !proposal?.id;
    if (step.id === 'delivery') contentDisabled = !proposal?.id || !isCartStepValid;
    if (step.id === 'resume')
      contentDisabled = !proposal?.id || !isDeliveryComplete || !isCartStepValid;

    // Step ordering gate: previous step must be completed,
    // EXCEPT cart only requires client (step 0) — credit analysis does NOT block cart,
    // and config only requires cart (step 2) — config does NOT block delivery/resume
    let orderingDisabled = false;
    if (index > 0) {
      let prerequisiteIndex = index - 1;
      if (step.id === 'cart') prerequisiteIndex = 0;
      if (step.id === 'delivery') prerequisiteIndex = 2;
      orderingDisabled = !isStepCompleted(stepIds[prerequisiteIndex]);
    }

    const disabled = contentDisabled || (isFutureStep && orderingDisabled);

    return {
      ...step,
      isActive,
      disabled,
      // Credit approval controls the indicator only; navigation keeps its existing gates.
      completed: step.id === 'credit-analysis' ? isCreditAnalysisCompleted : isCompleted,
      href: disabled ? '#' : step.href,
    };
  });
}
